<script lang="ts" setup>
import { computed } from 'vue';
import { useRoute } from '#app/composables/router';
import { canReadSpace, useEntitlement, usePermissionsStore } from '#layers/access/public';
import {
	getSpaceRouteParams,
	isSpaceRoute,
	useCurrentPageNotRendered,
	useSpaceEdit,
	useSpaces,
} from '#layers/spaces/public';
import SpacebarDivider from './Divider.vue';
import SpacebarItem from './Item.vue';

const route = useRoute();
const spaces = useSpaces();
const { can } = useEntitlement();
const permissions = usePermissionsStore();
const { createSpace } = useSpaceEdit();
const { currentPageNotRendered } = useCurrentPageNotRendered();

const currentSpaceRouteName = computed(() =>
	route.matched.find((record) => isSpaceRoute(record))?.name,
);

const activeSpaceIds = computed(() => {
	const ids = new Set<string>();
	const currentRouteName = currentSpaceRouteName.value;
	const notRenderedSpaceId = currentPageNotRendered.value?.space?.id;

	if (typeof currentRouteName === 'string') ids.add(currentRouteName);
	if (typeof notRenderedSpaceId === 'string') ids.add(notRenderedSpaceId);

	return ids;
});

const visibleSpaceItems = computed(() => spaces.value
	.filter((space) => can(canReadSpace(space.id)))
	.map((space) => ({
		active: activeSpaceIds.value.has(space.id),
		icon: space.icon,
		id: space.id,
		label: space.title,
		routeParams: getSpaceRouteParams(space),
	})));
</script>

<template>
	<template v-if="!permissions.isReady">
		<SpacebarDivider />

		<div
			v-for="n in 2"
			:key="n"
			data-testid="spacebar-skeleton"
			class="size-9 rounded-md bg-elevated"
		/>
	</template>

	<template v-else>
		<SpacebarDivider v-if="visibleSpaceItems.length > 0" />

		<SpacebarItem
			v-for="space in visibleSpaceItems"
			:key="space.id"
			:route-name="space.id"
			:route-params="space.routeParams"
			:label="space.label"
			:icon="space.icon"
			:active="space.active"
		/>
	</template>

	<SpacebarItem
		:label="$t('space.new_space_tooltip')"
		icon="lucide:plus"
		class="not-hover:opacity-50"
		@click="createSpace"
	/>
</template>
